import React, { useMemo } from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';

import { admissionEnquiryService } from '../../../services/admissions/admissionEnquiryService';
import { AdmissionEnquiry, AdmissionEnquiryInput } from '../../../types/admissions';
import SearchableSelect, { SelectOption } from '../../common/SearchableSelect';
import { formatPhone, isValidPhone } from '../form/admissionFormFields';
import { upcomingYears, yearLabel } from '../../../utils/academicYear';
import { ageOn, formatAge } from '../../../utils/age';
import useTimezone from '../../../hooks/useTimezone';

interface Props {
  open: boolean;
  /** Editing this enquiry; adding a new one when absent. */
  enquiry?: AdmissionEnquiry | null;
  classOptions: SelectOption[];
  onClose: () => void;
  onSaved: (enquiry: AdmissionEnquiry) => void;
}

const Section: React.FC<{ title: string }> = ({ title }) => (
  <Typography variant="subtitle2" fontWeight={600} color="text.secondary">{title}</Typography>
);

/** Add or edit an enquiry: who the child is, what admission they want, and how to reach the family. */
const EnquiryDialog: React.FC<Props> = ({ open, enquiry, classOptions, onClose, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { getToday } = useTimezone();
  const today = getToday();
  const years = useMemo(() => {
    const ys = upcomingYears();
    // An older enquiry keeps its year selectable.
    return enquiry && !ys.includes(enquiry.forYear) ? [enquiry.forYear, ...ys] : ys;
  }, [enquiry]);

  const formik = useFormik<AdmissionEnquiryInput>({
    enableReinitialize: true,
    initialValues: {
      childName: enquiry?.childName ?? '',
      dob: enquiry?.dob ? enquiry.dob.slice(0, 10) : '',
      forYear: enquiry?.forYear ?? years[1],
      classId: enquiry?.classId._id ?? '',
      parentName: enquiry?.parentName ?? '',
      parentPhone: enquiry ? formatPhone(enquiry.parentPhone) : '',
      note: enquiry?.note ?? '',
    },
    validationSchema: yup.object({
      childName: yup.string().trim().required('Enter the child’s name').max(120),
      dob: yup.string().nullable().test('past', 'Date of birth can’t be in the future', v => !v || v <= today),
      classId: yup.string().required('Choose a class'),
      parentName: yup.string().trim().required('Enter the parent’s name').max(120),
      parentPhone: yup.string().trim().required('Enter a phone number')
        .test('phone', 'Enter a valid phone number, e.g. 0300 1234567', v => !v || isValidPhone(v)),
      note: yup.string().max(1000),
    }),
    onSubmit: async (values) => {
      const payload = { ...values, dob: values.dob || null };
      try {
        const saved = enquiry
          ? await admissionEnquiryService.update(enquiry._id, payload)
          : await admissionEnquiryService.create(payload);
        enqueueSnackbar(enquiry ? 'Enquiry updated' : `${saved.childName} added to enquiries`, { variant: 'success' });
        onSaved(saved);
      } catch (err: any) {
        enqueueSnackbar(err?.message || 'Could not save the enquiry', { variant: 'error' });
      }
    },
  });

  const close = () => {
    if (formik.isSubmitting) return;
    onClose();
    formik.resetForm();
  };

  const field = (name: keyof AdmissionEnquiryInput) => ({
    name,
    value: formik.values[name] ?? '',
    onChange: formik.handleChange,
    onBlur: formik.handleBlur,
    error: !!formik.touched[name] && !!formik.errors[name],
    helperText: formik.touched[name] && formik.errors[name],
    fullWidth: true,
  });

  const age = formik.values.dob && formik.values.dob <= today ? formatAge(ageOn(formik.values.dob, today)) : null;

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <form onSubmit={formik.handleSubmit} noValidate>
        <DialogTitle component="div" sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={700}>{enquiry ? 'Edit enquiry' : 'Add enquiry'}</Typography>
          <Typography variant="caption" color="text.secondary">
            A family interested in admission. Call them when admissions for their year open.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <Section title="Child" />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 7 }}>
                <TextField label="Child’s name" {...field('childName')} />
              </Grid>
              <Grid size={{ xs: 12, sm: 5 }}>
                <TextField
                  label="Date of birth (optional)" type="date" {...field('dob')}
                  helperText={(formik.touched.dob && formik.errors.dob) || (age ? `${age} today` : undefined)}
                  InputLabelProps={{ shrink: true }} inputProps={{ max: today }}
                />
              </Grid>
            </Grid>

            <Divider />
            <Section title="Admission for" />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 5 }}>
                <TextField select label="Academic year" {...field('forYear')} onChange={e => formik.setFieldValue('forYear', Number(e.target.value))}>
                  {years.map(y => <MenuItem key={y} value={y}>{yearLabel(y)}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 7 }}>
                <SearchableSelect
                  label="Class"
                  value={formik.values.classId}
                  onChange={v => formik.setFieldValue('classId', v)}
                  options={classOptions}
                  placeholder="Choose a class"
                  size="medium"
                  fullWidth
                  error={!!formik.touched.classId && !!formik.errors.classId}
                  helperText={formik.touched.classId ? formik.errors.classId : undefined}
                />
              </Grid>
            </Grid>

            <Divider />
            <Section title="Parent" />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 7 }}>
                <TextField label="Parent’s name" {...field('parentName')} />
              </Grid>
              <Grid size={{ xs: 12, sm: 5 }}>
                <TextField label="Phone" placeholder="0300 1234567" inputMode="tel" {...field('parentPhone')} />
              </Grid>
            </Grid>

            <TextField
              label="Note (optional)" placeholder="e.g. Elder brother in Class 4, prefers morning shift"
              multiline minRows={2} {...field('note')}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={close} color="inherit" disabled={formik.isSubmitting}>Cancel</Button>
          <Button type="submit" variant="contained" disableElevation disabled={formik.isSubmitting}>
            {formik.isSubmitting ? 'Saving…' : enquiry ? 'Save changes' : 'Add enquiry'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EnquiryDialog;
